/**
 * VoiceDictation — mic button + language selector that transcribes the
 * teacher's speech (OpenAI Whisper) and appends it to the message.
 * Auto-detects language by default; teacher can force English/Kannada/Hindi.
 */
import { useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LANGS = [
  { value: "auto", label: "Auto-detect" },
  { value: "en", label: "English" },
  { value: "kn", label: "ಕನ್ನಡ" },
  { value: "hi", label: "हिन्दी" },
];

export function VoiceDictation({ onText, maxChars }) {
  const [lang, setLang] = useState("auto");
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => transcribe();
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied. Please allow mic permission.");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    setRecording(false);
  };

  const transcribe = async () => {
    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
    if (!blob.size) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "speech.webm");
      fd.append("language", lang);
      const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const text = (data.text || "").trim();
      if (!text) { toast.info("Didn't catch that — please try speaking again."); return; }
      onText(text, maxChars);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not transcribe. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2" data-testid="voice-dictation">
      <Select value={lang} onValueChange={setLang} disabled={recording || busy}>
        <SelectTrigger className="h-8 w-32 text-xs rounded-lg" data-testid="dictation-lang">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {LANGS.map((l) => <SelectItem key={l.value} value={l.value} className="text-xs" data-testid={`dictation-lang-${l.value}`}>{l.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button
        type="button" size="sm" variant={recording ? "destructive" : "outline"}
        onClick={recording ? stop : start} disabled={busy}
        className="h-8 rounded-lg text-xs" data-testid="dictation-mic"
      >
        {busy ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Transcribing…</>
          : recording ? <><Square className="h-3.5 w-3.5 mr-1" /> Stop</>
          : <><Mic className="h-3.5 w-3.5 mr-1" /> Speak</>}
      </Button>
    </div>
  );
}
